import React from "react";
import { LegalPage } from "@/components/legal/LegalPage";
import { ENV } from "@/config/env";

const LAST_UPDATED = "June 1, 2026";

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of the Terms",
    body: [
      `These Terms of Use ("Terms") govern your access to and use of ${ENV.APP_NAME} ("WhooLive", "we", "us" or "our"), including the WhooLive website and the official mobile applications for iOS and Android (together, the "Service").`,
      "By creating an account or otherwise using the Service, you agree to be bound by these Terms and by our Privacy Policy. If you do not agree, you must not use the Service.",
    ],
  },
  {
    id: "eligibility",
    title: "2. Eligibility & Age",
    body: [
      "The minimum age to create an account or use the Service is 18 years, because the Service includes live streaming and camera, microphone and user-generated content features. Where local law expressly permits use by a minor with the consent of a parent or legal guardian, such use is only allowed where that verifiable consent has been provided.",
      "By using the Service you represent that you meet these requirements. We may suspend or delete accounts that do not comply.",
    ],
  },
  {
    id: "account",
    title: "3. Your Account",
    body: [
      "You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. The same account may be used across the website and the official mobile apps. You must notify us immediately of any unauthorised use of your account.",
    ],
  },
  {
    id: "user-content",
    title: "4. User Content & Your Responsibility",
    body: [
      "WhooLive is a platform for user-generated content. You are solely and fully responsible for all content you publish, broadcast, stream, upload or otherwise share through the Service, including live video, audio, photos, event information and chat messages.",
      "You represent and warrant that you own or have all necessary rights, licences, permissions, authorisations and consents to share your content, and that your content does not infringe the rights of any third party or violate any applicable law. It is your sole responsibility to obtain any special permission or authorisation that may be required to film, stream or publish a place, event, performance or person.",
    ],
  },
  {
    id: "prohibited",
    title: "5. Prohibited Content & Conduct",
    body: [
      "You must not publish, broadcast, upload or share any content that is illegal, violent, pornographic or sexually explicit, defamatory, discriminatory, hateful, harassing, or that infringes the rights of third parties (including intellectual-property, privacy and image rights). Child sexual abuse material (CSAM) and any content that sexualises, exploits or endangers minors is strictly and absolutely forbidden and will be reported to the competent authorities.",
      "You must not engage in abuse, bullying or harassment of other users; impersonate any person or entity; misrepresent your affiliation with a person or entity; or post false, misleading or deceptive information. You must not attempt to disrupt, damage or gain unauthorised access to the Service or to other users' accounts or data.",
    ],
  },
  {
    id: "moderation",
    title: "6. Content Moderation & Enforcement",
    body: [
      "WhooLive may, at its discretion and to the extent permitted by law, monitor, review and moderate content. We may remove or disable access to any content, limit or disable features, and suspend or permanently terminate accounts that violate these Terms or applicable law, or where necessary to protect the Service, our users or third parties.",
      "Where required by law, in particular for content involving minors or other unlawful material, we may preserve relevant data and report it to the competent law-enforcement authorities.",
    ],
  },
  {
    id: "reporting",
    title: "7. Reporting Content & Users",
    body: [
      "If you come across content or behaviour that you believe violates these Terms or the law, you can report it to us so we can review it and take appropriate action. You may report content or other users through the reporting tools provided in the Service or by contacting us using the details below. We review reports and act on them in accordance with these Terms and applicable law.",
    ],
  },
  {
    id: "no-warranty",
    title: "8. No Warranty on User Content",
    body: [
      "Content published on WhooLive is created by users. WhooLive does not create, endorse, verify or control user content and does not guarantee the truthfulness, accuracy, completeness, quality, legality or reliability of any content published by users. Any reliance you place on such content is at your own risk.",
      "The Service is provided on an \"as is\" and \"as available\" basis. To the maximum extent permitted by law, we disclaim all warranties not expressly set out in these Terms.",
    ],
  },
  {
    id: "ip",
    title: "9. Intellectual Property",
    body: [
      "You retain the rights you hold in the content you share. By sharing content through the Service, you grant WhooLive a worldwide, non-exclusive, royalty-free licence to host, store, reproduce, transmit and display that content solely for the purpose of operating and providing the Service.",
      "The WhooLive name, logo, software and design elements are protected and may not be used without our prior written permission.",
    ],
  },
  {
    id: "liability",
    title: "10. Limitation of Liability & Indemnity",
    body: [
      "To the maximum extent permitted by law, WhooLive shall not be liable for any indirect, incidental, special or consequential damages, or for user content or the conduct of any user. Nothing in these Terms limits liability that cannot be limited under applicable law.",
      "You agree to indemnify and hold WhooLive harmless from any claim, liability, damage or expense arising from your content, your use of the Service, your breach of these Terms, or your failure to obtain any required permission or authorisation.",
    ],
  },
  {
    id: "termination",
    title: "11. Suspension & Termination",
    body: [
      "You may stop using the Service and delete your account at any time. We may suspend or terminate your access to the Service, with or without notice, if you breach these Terms, if required by law, or to protect the Service or its users. Provisions that by their nature should survive termination will continue to apply.",
    ],
  },
  {
    id: "changes",
    title: "12. Changes to These Terms",
    body: [
      "We may update these Terms from time to time. When we make material changes, we will update the \"Last updated\" date above and, where appropriate, notify you within the Service. Your continued use of the Service after changes take effect means you accept the updated Terms.",
    ],
  },
  {
    id: "contact",
    title: "13. Governing Law & Contact",
    body: [
      "These Terms are governed by the applicable laws of the operator's place of establishment, without prejudice to any mandatory consumer-protection rights you may have.",
      "The operator of the Service and data controller is Mario Mignacca (WhooLive). If you have any questions about these Terms or wish to report content, please contact us.",
    ],
  },
];

export default function Terms() {
  return (
    <LegalPage
      testidPrefix="terms"
      badgeLabel="Legal"
      title="Terms of Use"
      lastUpdated={LAST_UPDATED}
      intro={`These Terms of Use set out the rules for using ${ENV.APP_NAME} and the content shared on it. Please read them carefully.`}
      sections={SECTIONS}
      crossLink={{
        to: "/privacy",
        label: "Read our Privacy Policy",
        desc: "To understand how we handle your personal data, please read our Privacy Policy, which forms part of your agreement with WhooLive.",
      }}
    />
  );
}
